import { useTranslation } from 'react-i18next';
import {
  getSortedCalendars,
  isPrimaryCalendar,
} from '@/features/interactions/addToGoogle/calendar.lib';
import type { DecoratedIntegration } from '@/pages/Schedule/hooks/useIntegrations';
import { IntegrationType } from '@/types/gql.generated';
import { Icons, List, ListIcon, ListItem, Text } from '@/ui';

type Calendar = DecoratedIntegration['calendars'][number];

type Props = {
  integration: DecoratedIntegration;
};

export const CalendarList = ({ integration }: Props) => {
  const { t } = useTranslation('settingsModal', { keyPrefix: 'calendars' });
  const isOutlook = integration.type === IntegrationType.OutlookCalendar;
  const calendars = getSortedCalendars(integration).filter((calendar) => {
    // Outlook calendar ids are opaque (never equal the account email), so the
    // Google "primary calendar" heuristic never matches; surface the account's
    // writable calendars instead so their names are visible.
    if (isOutlook) {
      return !calendar.readonly;
    }
    return (
      calendar.scheduleCount > 0 ||
      calendar.plannerCount > 0 ||
      isPrimaryCalendar(integration, calendar)
    );
  });

  // A calendar can feed schedules, planners, or both; the two counts read as
  // one sentence so it's clear where disconnecting would have an effect.
  const getUsageLabel = ({ scheduleCount, plannerCount }: Calendar) => {
    const schedules = t('schedule_count', { count: scheduleCount });
    const planners = t('planner_count', { count: plannerCount });

    if (scheduleCount > 0 && plannerCount > 0) {
      return t('used_in_both', { schedules, planners });
    }
    if (scheduleCount > 0) {
      return t('used_in', { usage: schedules });
    }
    if (plannerCount > 0) {
      return t('used_in', { usage: planners });
    }
    return null;
  };

  return (
    <List ml="6">
      {calendars.map((calendar) => {
        const usageLabel = getUsageLabel(calendar);

        return (
          <ListItem data-calendar-uri={calendar.uri} key={calendar.uri}>
            <ListIcon as={Icons.Calendar} boxSize="14px" />
            {calendar.name}
            {usageLabel && (
              <Text as="span" fontSize="sm" fontWeight="bold" mx="2">
                &ndash; {usageLabel}
              </Text>
            )}
          </ListItem>
        );
      })}
    </List>
  );
};
